import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const CategoryFormModal = ({ 
  isOpen, 
  onClose, 
  onSubmit, 
  category = null, 
  mode = 'create',
  existingCategories = []
}) => {
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    description: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Common category codes with suggestions
  const commonCategories = [
    { name: 'Color Cosmetics', code: 'CC' },
    { name: 'Skincare', code: 'SC' },
    { name: 'Haircare', code: 'HC' },
    { name: 'Home & Fabric Care', code: 'HFC' },
    { name: 'Personal Care', code: 'PC' },
    { name: 'Baby Care', code: 'BC' },
    { name: 'Oral Care', code: 'OC' },
    { name: 'Men\'s Grooming', code: 'MG' },
    { name: 'Fragrances', code: 'FR' },
    { name: 'Health & Wellness', code: 'HW' }
  ];

  useEffect(() => {
    if (category && mode === 'edit') {
      setFormData({
        name: category?.name || '',
        code: category?.code || '',
        description: category?.description || ''
      });
    } else {
      setFormData({
        name: '',
        code: '',
        description: ''
      });
    }
    setErrors({});
  }, [category, mode, isOpen]);

  const handleInputChange = (field, value) => {
    if (field === 'code') {
      // Convert to uppercase and limit to 5 characters
      value = value?.toUpperCase()?.slice(0, 5);
    }
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.name?.trim()) {
      newErrors.name = 'Category name is required';
    }

    if (!formData?.code?.trim()) {
      newErrors.code = 'Category code is required';
    } else if (formData?.code?.length < 2) {
      newErrors.code = 'Category code must be at least 2 characters';
    } else if (formData?.code?.length > 5) {
      newErrors.code = 'Category code must be at most 5 characters';
    } else if (!/^[A-Z]+$/?.test(formData?.code)) {
      newErrors.code = 'Category code must contain only uppercase letters';
    }

    // Check for duplicate codes (excluding current category in edit mode)
    const duplicateCode = existingCategories?.find(c => 
      c?.code?.toUpperCase() === formData?.code?.toUpperCase() && 
      (mode === 'create' || c?.id !== category?.id)
    );
    if (duplicateCode) {
      newErrors.code = 'This category code is already in use';
    }

    // Check for duplicate names (excluding current category in edit mode)
    const duplicateName = existingCategories?.find(c => 
      c?.name?.toLowerCase()?.trim() === formData?.name?.toLowerCase()?.trim() && 
      (mode === 'create' || c?.id !== category?.id)
    );
    if (duplicateName) {
      newErrors.name = 'This category name is already in use';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      await onSubmit(formData);
      onClose();
    } catch (error) {
      console.error('Error submitting category form:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleQuickSelect = (selectedCategory) => {
    setFormData({
      name: selectedCategory?.name,
      code: selectedCategory?.code,
      description: getDefaultDescription(selectedCategory?.name)
    });
  };

  const getDefaultDescription = (categoryName) => {
    switch (categoryName) {
      case 'Color Cosmetics':
        return 'Makeup and color products';
      case 'Skincare':
        return 'Skin care and treatment products';
      case 'Haircare':
        return 'Hair care and styling products';
      case 'Home & Fabric Care':
        return 'Household cleaning products';
      case 'Personal Care':
        return 'Personal hygiene and care products';
      case 'Baby Care':
        return 'Products for babies and infants';
      case 'Oral Care':
        return 'Dental and oral hygiene products';
      case 'Men\'s Grooming':
        return 'Male grooming and personal care';
      case 'Fragrances':
        return 'Perfumes and fragrance products';
      case 'Health & Wellness':
        return 'Health and wellness products';
      default:
        return '';
    }
  };

  // Filter out already used categories for quick select
  const availableQuickCategories = commonCategories?.filter(cat => 
    !existingCategories?.some(existing => 
      existing?.code === cat?.code || existing?.name?.toLowerCase() === cat?.name?.toLowerCase()
    )
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-lg border border-border shadow-elevated w-full max-w-md max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b border-border">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-secondary/10 rounded-lg flex items-center justify-center">
                <Icon name="Folder" size={20} className="text-secondary" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-foreground">
                  {mode === 'edit' ? 'Edit Category' : 'Add New Category'}
                </h2>
                <p className="text-sm text-muted-foreground">
                  {mode === 'edit' ? 'Update category information' : 'Create a new category with unique code'}
                </p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="text-muted-foreground hover:text-foreground"
            >
              <Icon name="X" size={20} />
            </Button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Quick Select (only in create mode) */}
          {mode === 'create' && availableQuickCategories?.length > 0 && (
            <div>
              <label className="text-sm font-medium text-foreground mb-2 block">
                Quick Select (Optional)
              </label>
              <div className="grid grid-cols-1 gap-2 max-h-32 overflow-y-auto">
                {availableQuickCategories?.slice(0, 6)?.map(cat => (
                  <button
                    key={cat?.code}
                    type="button"
                    onClick={() => handleQuickSelect(cat)}
                    className="text-left p-2 rounded border border-border hover:bg-accent/5 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-foreground">{cat?.name}</span>
                      <span className="text-xs text-muted-foreground font-mono">{cat?.code}</span>
                    </div>
                  </button>
                ))}
              </div>
              <div className="mt-3 border-t border-border pt-4">
                <p className="text-xs text-muted-foreground">Or create a custom category below:</p>
              </div>
            </div>
          )}

          <Input
            label="Category Name"
            type="text"
            placeholder="Enter category name"
            value={formData?.name}
            onChange={(e) => handleInputChange('name', e?.target?.value)}
            error={errors?.name}
            required
          />

          <div>
            <Input
              label="Category Code"
              type="text"
              placeholder="2-5 letters (e.g., CC, SC, HFC)"
              value={formData?.code}
              onChange={(e) => handleInputChange('code', e?.target?.value)}
              error={errors?.code}
              required
              maxLength={5}
            />
            <p className="text-xs text-muted-foreground mt-1">
              A 2-5 letter code to identify this category. Examples: CC = Color Cosmetics, HFC = Home & Fabric Care
            </p>
          </div>

          <Input
            label="Description (Optional)"
            type="text"
            placeholder="Brief description of the category"
            value={formData?.description}
            onChange={(e) => handleInputChange('description', e?.target?.value)}
          />

          {/* Code Preview */}
          {formData?.code?.trim() && (
            <div className="p-3 bg-accent/10 border border-accent/20 rounded-lg">
              <div className="flex items-center gap-2">
                <Icon name="Hash" size={14} className="text-accent" />
                <span className="text-sm font-medium text-accent">KV ID Preview:</span>
                <span className="font-mono text-accent">XXX-{formData?.code}-001</span>
              </div>
            </div>
          )}

          {/* Form Actions */}
          <div className="flex items-center justify-end gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="default"
              loading={isSubmitting}
              iconName={mode === 'edit' ? 'Save' : 'Plus'}
              iconPosition="left"
            >
              {mode === 'edit' ? 'Update Category' : 'Add Category'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CategoryFormModal;